import React, { useState, useContext } from 'react';
import { Avatar, Box, VStack, Text, Button, Flex, Icon, IconButton, useToast } from '@chakra-ui/react';
import { FiAlertTriangle, FiChevronLeft } from 'react-icons/fi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { redeemCollab } from '../../../services/firebaseService';
import { collabApi } from '../../../services/api/collab';
import { CollabDataContext } from '../../../contexts/collabDataContext';
import { InfluencerCampaignDataContext } from '../../../contexts/InfluencerCampaignDataContext';
import { InfluencerDataContext } from '../../../contexts/InfluencerDataContext';
import { ClientDataContext } from 'contexts/ClientDataContext';
import { isInAppCredit, isClientHandledCredit } from '../utils/collabUtils';
import { ff } from '../../../theme';
import { formatEarningsShort } from '../../../utils/earningsUtils';
import { toDate } from '../../../utils/dateUtils';
import { StickyCtaBar } from '../../../components/ui';
import { openCrispChat } from '../../../components/FrostedSupportButton';
import { getInfluencerDisplayName } from '../../../utils/influencerUtils';
import { useProductSelectionGuard } from '../utils/useProductSelectionGuard';

const RedeemRewardPage: React.FC = () => {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');

  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const collabData = useContext(CollabDataContext);
  const campaignData = useContext(InfluencerCampaignDataContext);
  const { influencerData } = useContext(InfluencerDataContext);
  const clients = useContext(ClientDataContext);
  const collab = collabData.find(c => c.id === id);
  const campaign = id ? campaignData[id] : undefined;
  // A brand can add an optional product to a live campaign; the endpoint below refuses a collab
  // that owes a selection. Send the creator to make one instead of failing with a 400.
  const goToProductSelection = useProductSelectionGuard(collab, campaign);
  const client = clients[campaign?.clientID];

  const handleConfirmRedemption = async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      if (isInAppCredit(collab, campaign)) {
        await redeemCollab(id);
        // Post-visit check-in is scheduled up front at collab-scheduling time (off the
        // visit date), so nothing to schedule here on redemption.
        navigate(`/collabs/view-reward?id=${id}`);
      } else {
        if (goToProductSelection()) return;
        await collabApi.advanceCollabToContentSubmission({
          collabId: collab.id,
          skipRedeem: true,
          requireDraftApproval: campaign?.requireDraftApproval
        });
        navigate(-1);
      }
      toast({
        title: "Reward Redeemed!",
        description: `You've successfully redeemed. Enjoy!`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
     
    } catch (error) {
      console.error('Error redeeming collab:', error);
      toast({
        title: "Error",
        description: "There was an error redeeming your reward. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRedeemLater = () => {
    navigate('/collabs');
  };

  if (!collab || !campaign) {
    return <Box>Loading...</Box>;
  }

  const inApp = isInAppCredit(collab, campaign);
  const clientHandled = isClientHandledCredit(collab, campaign);
  const showCash = collab.cash > 0;
  const showCredit = inApp || clientHandled;
  // Board hero is 120px; shrink as the figure grows so it never overflows a 390px screen.
  const heroSize = (text: string) => (text.length <= 4 ? '120px' : text.length <= 6 ? '88px' : '64px');
  const cashText = formatEarningsShort(collab.cash);
  const creditText = formatEarningsShort(collab.credit);

  const initials = (client?.accountName || 'R').split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const visit = toDate(collab.scheduledCollabDate);
  const visitLine = [
    visit && `${visit.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} · ${visit.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`,
    collab.partySize ? `party of ${collab.partySize}` : null,
  ].filter(Boolean).join(' · ');
  const kindLabel = inApp ? 'In-app credit' : clientHandled ? 'Credit at the till' : 'Cash reward';
  const headline = inApp ? 'Credit in the app' : clientHandled ? `Take ${creditText} off the bill` : 'Cash to the creator';
  const staffLine = inApp
    ? `Show this to restaurant staff. ${creditText} of FoodFluence credit is used on your visit. Nothing to do at the till.`
    : clientHandled
      ? campaign?.creditInstructions || 'Reach out to the brand through your inbox for credit details.'
      : 'Cash is paid out once your content is verified.';
  const heroText = showCredit ? creditText : cashText;
  const label = { fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' as const, color: '#B4B4B8' };

  return (
    <Box minH="100svh" bg="white" color={ff.ink} fontFamily="display">
      <Box maxW={{ base: '560px', lg: '720px' }} mx="auto" pb="160px">
        <Flex align="center" gap="8px" pt="max(12px, env(safe-area-inset-top))" px="12px">
          <IconButton
            aria-label="Back"
            icon={<FiChevronLeft size={22} strokeWidth={2.4} />}
            onClick={handleRedeemLater}
            variant="unstyled"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flex="none"
            w="44px"
            minW="44px"
            h="44px"
            borderRadius="999px"
            color={ff.ink}
          />
          <Text flex={1} fontSize="13px" fontWeight={600} letterSpacing="0.08em" textTransform="uppercase" color="#55555A" lineHeight="normal">
            Redeem reward
          </Text>
          <Button
            onClick={openCrispChat}
            aria-label="Support"
            variant="unstyled"
            display="flex"
            alignItems="center"
            flex="none"
            h="44px"
            px="14px"
            borderRadius="999px"
            border="1.5px solid"
            borderColor={ff.ink}
            fontSize="14px"
            fontWeight={600}
            color={ff.ink}
          >
            Support
          </Button>
        </Flex>

        <VStack align="stretch" spacing="16px" px="20px" pt="12px">
          <Flex align="center" gap="14px">
            <Avatar
              src={client?.instagramProfilePicture}
              name={client?.accountName}
              boxSize="52px"
              borderRadius="14px"
              bg="#E3E8EF"
              color={ff.ink}
              flex="none"
              sx={{ '& > [role=img]': { fontSize: '17px', fontWeight: 600 }, img: { borderRadius: '14px' } }}
              getInitials={() => initials}
            />
            <VStack align="stretch" spacing="3px" flex={1} minW={0}>
              <Text fontSize="22px" fontWeight={600} lineHeight="1.1" noOfLines={2}>{client?.accountName || 'the restaurant'}</Text>
              {visitLine && <Text fontSize="15px" lineHeight="normal" color={ff.body}>{visitLine}</Text>}
            </VStack>
          </Flex>

          <Flex role="alert" gap="12px" align="flex-start" p="16px" borderRadius="18px" bg="#FFF1D6" color="#5C3900">
            <Icon as={FiAlertTriangle} boxSize="26px" flex="none" strokeWidth={2.2} />
            <VStack align="stretch" spacing="4px">
              <Text as="strong" fontSize="20px" lineHeight="1.2">Only continue if you are at the restaurant!</Text>
              {showCredit && <Text fontSize="15px" lineHeight="1.4">Show this screen to a staff member. You can only redeem once.</Text>}
            </VStack>
          </Flex>

          {(showCash || showCredit) && (
            <VStack as="section" aria-label="Reward" align="stretch" spacing="10px" p="22px 20px" borderRadius="24px" bg={ff.ink} color="white">
              <Text fontSize="15px" fontWeight={600} letterSpacing="0.1em" textTransform="uppercase" color="#B4B4B8" lineHeight="normal">{kindLabel}</Text>
              <Text textStyle="display" fontSize={heroSize(heroText)} lineHeight={0.85} color={ff.lime} textTransform="none" letterSpacing="-0.01em" whiteSpace="nowrap">
                {heroText}
              </Text>
              <Text textStyle="display" fontSize="30px" lineHeight={1.12} color="white">{headline}</Text>
              <Text fontSize="17px" lineHeight="1.4" color="#D6D6D9">{staffLine}</Text>
              {showCredit && showCash && (
                <Text fontSize="17px" lineHeight="1.4" color="#D6D6D9">
                  Plus {cashText} cash, paid out once your content is verified.
                </Text>
              )}
              <Flex mt="4px" pt="14px" borderTop="1px solid #2A2A2C" justify="space-between" gap="12px">
                <VStack align="stretch" spacing="3px">
                  <Text {...label} lineHeight="normal">Creator</Text>
                  <Text fontSize="18px" fontWeight={600} lineHeight="normal">{getInfluencerDisplayName(influencerData)}</Text>
                </VStack>
              </Flex>
            </VStack>
          )}
        </VStack>
      </Box>

      <StickyCtaBar noNav>
        <Button
          variant="lime"
          w="100%"
          h="56px"
          borderRadius="999px"
          fontSize="17px"
          fontWeight={600}
          onClick={handleConfirmRedemption}
          isLoading={isLoading}
          loadingText="Confirming..."
          aria-label="Redeem now"
        >
          Confirm redemption
        </Button>
      </StickyCtaBar>
    </Box>
  );
};

export default RedeemRewardPage;
